import * as React from 'react';
import { JRCInputFieldProps } from '../Common/JRCFormFieldRenderer.types';
import { DataCy } from '../../../../types/dataAttributes';
import { FieldValues } from 'react-hook-form';
import { InputFile, NativeInputFileProps } from '../JRCInputFile/JRCInputFile';
import GroupBackground from './group-background.png';
import {
    DropZone,
    DropZoneButton,
    DropZoneImage,
    Grid,
    ImageButton,
    Img,
    InputFileWrapper,
    ReadOnlyImg,
} from './JRCInputImage.styles';
import jamespot, { jFileLittle } from 'jamespot-user-api';
import { JRCFormFieldRenderer } from '../Common/JRCFormFieldRenderer';
import { JRCIconButton } from '../../../JRCIconButton/JRCIconButton';
import { Actions } from '../../../JRCCard/JRCCardImg';
import { JRCCropImage } from '../../../JRCCropImage/JRCCropImage';
import { Area } from '../../../JRCCropImage/JRCCropImage';
import { strToTimestamp } from 'utils/utils.date';

export type DropZoneDimensions = {
    width: number;
    height: number;
};

export type NativeInputImageProps = DataCy &
    Omit<React.ComponentPropsWithoutRef<'input'>, 'value' | 'onChange'> & {
        // token is MANDATORY but can be given asynchronously
        token?: string | undefined;
        attrName?: string;
        dzDimensions?: DropZoneDimensions;
        onLoading?: (state: boolean) => void;
        aspectRatio?: number | undefined;
        defaultImages?: {
            imgs: Array<{
                src: string;
                alt: string;
            }>;
            position?: 'right' | 'bottom';
            columnCount: number;
        };
        readOnly?: boolean;
        readOnlyImg?: string;
        // override default html value declaration. { type, id, timestamp? } are necessary at initialization time
        value: jFileLittle | undefined;
        // override default html onChange declaration
        onChange: (files: jFileLittle | undefined) => void;
        disableCrop?: boolean;
        buttonsPosition?: 'inside' | 'under';
        allowCustomRatio?: boolean;
        zIndex?: number;
    };

export type JRCInputImageProps<T extends FieldValues = FieldValues> = JRCInputFieldProps<T> &
    Omit<NativeInputImageProps, 'value' | 'onChange'>;

function isImageValid(e: React.DragEvent<HTMLButtonElement>, accept: Array<string>) {
    if (e.dataTransfer.items) {
        const file = Array.from(e.dataTransfer.items || [])?.[0];
        if (file && file.kind === 'file' && accept.some((extension) => file.type.endsWith(extension))) {
            return true;
        }
    } else {
        const file = Array.from(e.dataTransfer.files || [])?.[0];
        if (file && accept.some((extension) => file.type.endsWith(extension))) {
            return true;
        }
    }

    return false;
}

function getDraggedImage(e: React.DragEvent<HTMLButtonElement>, accept: Array<string>) {
    if (e.dataTransfer.items) {
        const file = Array.from(e.dataTransfer.items || [])?.[0];
        if (file && file.kind === 'file' && accept.some((extension) => file.type.endsWith(extension))) {
            return file.getAsFile() || undefined;
        }
    } else {
        const file = Array.from(e.dataTransfer.files || [])?.[0];
        if (file && accept.some((extension) => file.type.endsWith(extension))) {
            return file;
        }
    }

    return;
}

const InputImageElement = (
    {
        onChange,
        onLoading,
        dzDimensions,
        value,
        disableCrop,
        buttonsPosition = 'inside',
        allowCustomRatio,
        zIndex,
        ...props
    }: NativeInputImageProps,
    ref: React.ForwardedRef<{ click: () => void }>,
) => {
    const [isDragged, setIsDragged] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [loading, setLoading] = React.useState(false);
    const [openCrop, setOpenCrop] = React.useState(false);
    const [accept] = React.useState(
        props.accept?.split(',').map((acc) => acc.replace(/\./g, '')) || ['jpg', 'jpeg', 'png'],
    );
    const [aspect] = React.useState(props.aspectRatio);
    const [cropIndex, setCropIndex] = React.useState(0);

    const previewUrl = React.useMemo(() => {
        if (!value) return undefined;
        return `/imagecache/original/${value.type}/${value.id}.png?_=${
            value.dateModified ? strToTimestamp(value.dateModified) : Date.now()
        }-${cropIndex}`;
    }, [value, cropIndex]);

    const attrName = props.attrName ?? props.name;

    React.useImperativeHandle(
        ref,
        () => {
            return {
                click() {
                    inputRef?.current?.click?.();
                },
            };
        },
        [],
    );

    function dragOver(e: React.DragEvent<HTMLButtonElement>) {
        e.preventDefault();
        e.stopPropagation();

        if (!isDragged && isImageValid(e, accept)) setIsDragged(true);
    }

    function dragLeave(e: React.DragEvent<HTMLButtonElement>) {
        e.preventDefault();
        e.stopPropagation();
        setIsDragged(false);
    }

    function drop(e: React.DragEvent<HTMLButtonElement>) {
        e.preventDefault();
        e.stopPropagation();
        setIsDragged(false);

        const image = getDraggedImage(e, accept);

        if (image && attrName && props.token) {
            setLoading(true);
            jamespot.file
                .upload({
                    attrName: attrName,
                    file: image,
                    token: props.token,
                })
                .then((res) => onChange(res.response.result))
                .finally(() => setLoading(false));
        }
    }

    function click() {
        inputRef?.current?.click?.();
    }

    const loadImage: NativeInputFileProps['onChange'] = (res) => {
        if (res.length === 1 && res[0].result) {
            onChange(res[0].result);
        }
    };

    function clickDefaultImage(url: string) {
        if (!attrName || !props.token) return;
        setLoading(true);

        jamespot.file
            .upload({
                attrName: attrName,
                url,
                token: props.token,
            })
            .then((res) => onChange(res.response.result))
            .finally(() => setLoading(false));
    }

    function cropImage(cropConfig: Area & { rotation: number }) {
        if (value) {
            const cropSettings = {
                x: cropConfig.x,
                y: cropConfig.y,
                w: cropConfig.width,
                h: cropConfig.height,
                rotation: cropConfig.rotation,
                killSize: true,
            };

            jamespot.file.update(value.id, value.type, cropSettings).then((res) => {
                setOpenCrop(false);
                setCropIndex(cropIndex + 1);
                onChange({
                    ...value,
                    ...res.result,
                });
            });
        }
    }

    function deleteImage() {
        if (value && attrName && props.token) {
            /**
             * if an image is a content image, we need to delete it with deleteImage. It is permanent
             * otherwise, deleteFile is great and it is not permanent
             */
            if (attrName === 'image' && value?.type !== 'fileTmp') {
                jamespot.file.deleteImage(value.id, value?.type).then(() => {
                    onChange(undefined);
                });
            } else {
                jamespot.file.deleteFile(value.id, attrName, props.token).then(() => {
                    onChange(undefined);
                });
            }
        }
    }

    React.useEffect(() => {
        onLoading?.(loading);
    }, [loading, onLoading]);

    const defaultImages = props.defaultImages ?? { imgs: [], columnCount: 1 };

    return (
        <>
            {props.readOnly ? (
                <ReadOnlyImg src={props.readOnlyImg} alt="" />
            ) : (
                <>
                    <InputFileWrapper>
                        <InputFile
                            {...props}
                            accept={accept.map((acc) => '.' + acc).join(',')}
                            onLoading={setLoading}
                            onChange={loadImage}
                            ref={inputRef}
                            value={value as any}
                        />
                    </InputFileWrapper>
                    <Grid position={defaultImages.position} columnCount={defaultImages.columnCount}>
                        <DropZone
                            position={defaultImages.position}
                            columnCount={defaultImages.columnCount}
                            dzDimensions={dzDimensions}>
                            <DropZoneButton
                                role="input"
                                type="button"
                                aspectRatio={aspect}
                                className={[
                                    isDragged ? 'is-dragged' : undefined,
                                    loading ? 'is-loading' : undefined,
                                ].join(' ')}
                                onDragOver={dragOver}
                                onDragEnter={dragOver}
                                onDragEnd={dragLeave}
                                onDragLeave={dragLeave}
                                onDrop={drop}
                                onClick={click}
                                disabled={loading}
                                data-cy={props.dataCy}>
                                {value ? (
                                    value.mimetype !== 'image/gif' ? (
                                        <DropZoneImage src={previewUrl} alt="" />
                                    ) : (
                                        <p>{value.title}</p>
                                    )
                                ) : (
                                    <>
                                        <img src={GroupBackground} alt="" style={{ zIndex: 2 }} />
                                        <p style={{ zIndex: 2 }}>{accept.join(', ')}</p>
                                    </>
                                )}
                            </DropZoneButton>
                            {value && (
                                <Actions position={{ y: buttonsPosition === 'inside' ? 'bottom' : 'under' }}>
                                    <JRCIconButton
                                        bgBaseGrey={1}
                                        type="button"
                                        icon="icon-trash"
                                        tooltip={{ description: 'GLOBAL_Delete' }}
                                        onClick={(e) => {
                                            deleteImage();
                                            e?.preventDefault();
                                        }}
                                    />
                                    {!disableCrop && value.mimetype !== 'image/gif' && (
                                        <JRCIconButton
                                            bgBaseGrey={1}
                                            type="button"
                                            icon="icon-crop"
                                            tooltip={{ description: 'CROP_Crop' }}
                                            onClick={(e) => {
                                                setOpenCrop(true);
                                                e?.preventDefault();
                                            }}
                                        />
                                    )}
                                </Actions>
                            )}
                        </DropZone>
                        {defaultImages.imgs.map(({ src, alt }) => (
                            <ImageButton
                                key={src}
                                type="button"
                                aspectRatio={aspect}
                                onClick={() => clickDefaultImage(src)}>
                                <Img src={src} alt={alt} />
                            </ImageButton>
                        ))}
                    </Grid>
                    {value && openCrop && (
                        <JRCCropImage
                            zIndex={zIndex}
                            open={openCrop}
                            aspectRatio={aspect}
                            allowCustomRatio={allowCustomRatio}
                            src={previewUrl}
                            save={cropImage}
                            closeHandler={() => {
                                setOpenCrop(false);
                            }}
                        />
                    )}
                </>
            )}
        </>
    );
};

const InputImage = React.forwardRef(InputImageElement);

/**
 * Component used as a <input type="file"/>
 * @param props JRCInputFieldProps
 * validation props: required
 * @returns JSX.Element
 */
export function JRCInputImage<T extends FieldValues = FieldValues>(props: JRCInputImageProps<T>) {
    return (
        <JRCFormFieldRenderer
            {...props}
            htmlFor={props.name}
            renderFunction={({ value, ...field }) => <InputImage {...props} {...field} value={value} />}
        />
    );
}
